import * as React from "react"

import { ConnectBrokerImportMethodCard } from "@/components/Broker/ConnectBrokerImportMethodCard"
import type { BrokerCapabilities } from "@/services/brokerIntegrationApi"
import { resolveBrokerDisplayName } from "@/utils/customCsvAccountDisplay"
import {
  CONNECT_BROKER_AUTO_SYNC_LABEL,
  resolveConnectBrokerImportMethods,
  type ConnectBrokerImportMethodId,
  type ConnectBrokerImportMethodOption,
} from "@/views/Dashboard/Profile/connectBroker/connectBrokerImportMethods"
import { ConnectBrokerSelectedBrokerHeading } from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerSelectedBrokerHeading"

const IMPORT_METHOD_IDS: ConnectBrokerImportMethodId[] = ["auto_sync", "file_upload"]

export type ConnectBrokerSelectImportMethodStepProps = {
  capability: BrokerCapabilities
  selectedMethodId: ConnectBrokerImportMethodId | null
  onSelectMethod: (methodId: ConnectBrokerImportMethodId) => void
}

export function ConnectBrokerSelectImportMethodStep({
  capability,
  selectedMethodId,
  onSelectMethod,
}: ConnectBrokerSelectImportMethodStepProps): React.ReactElement {
  const displayName = resolveBrokerDisplayName(capability)

  const methods = React.useMemo(
    () =>
      resolveConnectBrokerImportMethods(capability).filter((method) =>
        IMPORT_METHOD_IDS.includes(method.id)
      ),
    [capability]
  )

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <ConnectBrokerSelectedBrokerHeading
        brokerName={capability.broker_name}
        displayName={displayName}
        title="Select import method"
        description={`Choose how you want to bring trades into Tessera for ${displayName}.`}
      />

      {methods.length === 0 ? (
        <p className="py-6 text-center font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
          {CONNECT_BROKER_AUTO_SYNC_LABEL} and file upload are not available for this broker yet.
        </p>
      ) : (
        <div
          className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2"
          data-connect-import-method-grid=""
          role="listbox"
          aria-label="Import methods"
        >
          {methods.map((method: ConnectBrokerImportMethodOption) => (
            <ConnectBrokerImportMethodCard
              key={method.id}
              methodId={method.id}
              title={method.title}
              description={method.description}
              selected={selectedMethodId === method.id}
              onSelect={() => onSelectMethod(method.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
